import type { SessionMessageModel } from '@mindverse/accessor-open';
import { SENDER_TYPE, SessionErrorCode, SessionMessageType } from '@mindverse/accessor-open';
import { useMemoizedFn } from 'ahooks';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { ISourceCustomSoftMemory, TNotice } from '@/types/wsNotice';
import { ENoticeType } from '@/types/wsNotice';
import { EVENT } from '@/utils/event';

import type { MessageType } from '../components/chat/model/message-item';
import type MessageItem from '../components/chat/model/message-item';
import {
  EMessageAction,
  MessageItemType,
  transformMsg
} from '../components/chat/model/message-item';
import ContextBasicInfo from '../context/BasicInfo';
import type {
  WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_COMMON_ACTION,
  WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_MEMBER_JOIN
} from '../interface/chat-content';
import { WS_SYSTEM_MULTIPLE_TEMPLATE_TYPE } from '../interface/chat-content';
import { INNER_EVENT } from '../utils/utils';
import useMindStorage, { EStorageKey } from './useMindStorage';

export interface MessageModel {
  messageId: string;
  type: MessageItemType;
  sendUserId: string;
  timestamp?: number;
  list: MessageType[];
}

export interface IProps {
  messageList?: SessionMessageModel[];
  receivedNewMsgCallback: (msgList: SessionMessageModel[]) => void;
}

export interface IResult {
  msgList: MessageModel[];
  appendReceivedMsg: (newMsgList: SessionMessageModel[]) => void;
  clearMsgList: () => void;
  appendUnShowedMsgId: (msg: { messageId: string; type: MessageItemType }) => void;
}

export function useSessionMsgList(props: IProps): IResult {
  const { receivedNewMsgCallback, messageList } = props;
  const { userId } = useContext(ContextBasicInfo);

  // 这个是为了快速找到是否存在某个消息，空间换时间，后端 seqId 是雪花，前端的是 uuid + timestamp 所以不会重复
  const msgMapRef = useRef<Record<string, MessageType>>({});
  const [msgList, setMsgList] = useState<MessageModel[]>([]);

  const [answerStopped, setAnswerStopped] = useMindStorage(EStorageKey.ANSWER_STOPPED);
  const answerStoppedRef = useRef<boolean>(false);
  const [unShowedMsgIdList, setUnShowedMsgIdList] = useState<
    {
      messageId: string;
      type: MessageItemType;
    }[]
  >([]);
  // 存储stop期间umm继续传输过来的消息seqId
  const unShowedSeqIdListRef = useRef<string[]>([]);

  const [sessionId] = useMindStorage(EStorageKey.SESSION_ID);
  const [sessionInfo, setSessionInfo] = useMindStorage(EStorageKey.SESSION_INFO);

  useEffect(() => {
    answerStoppedRef.current = !!answerStopped;
  }, [answerStopped]);

  const appendUnShowedMsgId = useCallback((data: { messageId: string; type: MessageItemType }) => {
    const { messageId, type } = data;

    setUnShowedMsgIdList((origin) => {
      return [
        ...origin,
        {
          messageId,
          type
        }
      ];
    });
  }, []);

  const checkUnShowedMsg = useCallback(
    (newMsgList: MessageType[]) => {
      newMsgList.forEach((msg) => {
        // 没有 subCode，或者说 subCode 不是 continue
        if (
          msg.type === SessionMessageType.sys &&
          (msg as MessageItem).subCode !== '' &&
          (msg as MessageItem).subCode !== undefined &&
          (msg as MessageItem).subCode !== SessionErrorCode.MESSAGE_LENGTH_LIMIT
        ) {
          appendUnShowedMsgId({ messageId: msg.messageId, type: MessageItemType.umm });
        }
      });
    },
    [appendUnShowedMsgId]
  );

  const appendReceivedMsg = useCallback(
    (newMsgList: SessionMessageModel[]) => {
      // 最后一条内容的 index = -1 说明停止了
      const notAddLastMessage = newMsgList.at(-1);

      if (
        notAddLastMessage &&
        notAddLastMessage.index === -1 &&
        notAddLastMessage.type === SessionMessageType.msg
      ) {
        answerStoppedRef.current = false;
        setAnswerStopped(false);

        const tempTemplate = notAddLastMessage.multipleData?.[0].modal.template;
        const template = JSON.parse(tempTemplate || `{}`);

        if (template.templateName === WS_SYSTEM_MULTIPLE_TEMPLATE_TYPE.CircleMemberJoin) {
          const params = template.params as WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_MEMBER_JOIN;

          if (!sessionInfo) {
            return;
          }

          const existingUserIds = new Set(sessionInfo.members.map((m) => m.userId));
          const newMembers = params.members.filter((m) => !existingUserIds.has(m.id));

          if (newMembers.length > 0) {
            setSessionInfo({
              ...sessionInfo,
              members: [
                ...sessionInfo.members,
                ...newMembers.map((m) => ({
                  userId: m.id,
                  name: m.username,
                  avatar: m.avatar || '',
                  route: m.route
                }))
              ]
            });
          }
        } else if (template.templateName === WS_SYSTEM_MULTIPLE_TEMPLATE_TYPE.CircleMemberQuit) {
          const params = template.params as WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_COMMON_ACTION;

          if (!sessionInfo) {
            return;
          }

          const existingUserIds = new Set(sessionInfo.members.map((m) => m.userId));
          const newMember = !existingUserIds.has(params.targets[0].id);

          if (newMember) {
            const filteredMembers = sessionInfo.members.filter(
              (m) => m.userId !== params.targets[0].id
            );

            setSessionInfo({
              ...sessionInfo,
              members: filteredMembers
            });
          }
        }
      }

      // Stop的时候，umm给过来的消息前端做拦截隐藏处理
      if (answerStoppedRef.current && notAddLastMessage?.sender === SENDER_TYPE.umm) {
        unShowedSeqIdListRef.current = [
          ...unShowedSeqIdListRef.current,
          notAddLastMessage.seqId || ''
        ];

        return;
      }

      const notExistMsgList = newMsgList.filter((item) => {
        const seqId = item.seqId;

        if (unShowedSeqIdListRef.current.includes(seqId)) {
          return false;
        }

        if (seqId in msgMapRef.current) {
          const exist = !!msgMapRef.current[seqId];

          return !exist;
        }

        return true;
      });

      // 如果有新消息，告诉外面之后会增加谁，用于处理声音等能力
      if (notExistMsgList.length !== 0) {
        console.warn('aaa notExistMsgList', notExistMsgList);
        receivedNewMsgCallback(
          notExistMsgList.filter(
            (e) => !(e.sender === SENDER_TYPE.client && e.sendUserId === userId)
          )
        );
      }

      const transformedMsg: MessageType[] = [];

      notExistMsgList.forEach((item) => {
        const newMsg = transformMsg(item);

        if (newMsg) {
          msgMapRef.current[item.seqId] = newMsg;
          transformedMsg.push(newMsg);
        }
      });

      checkUnShowedMsg(transformedMsg);

      setMsgList((origin) => {
        const newValue = [...origin];

        transformedMsg.forEach((item) => {
          const messageId = item.messageId;

          let hasMessageId = false;

          for (let index = newValue.length - 1; index >= 0; index--) {
            const element = newValue[index];

            if (element.messageId === messageId && element.type === item.sender) {
              hasMessageId = true;

              element.list = [...element.list, item];
              newValue[index] = { ...element };

              break;
            }
          }

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (!hasMessageId) {
            const newMessageModel: MessageModel = {
              messageId,
              type: item.sender,
              sendUserId: item.sendUserId,
              list: [item]
            };

            newValue.push(newMessageModel);
          }
        });

        return newValue;
      });
    },
    [checkUnShowedMsg, receivedNewMsgCallback, setAnswerStopped, userId]
  );

  const unShiftMsg = useCallback(
    (msgItem: MessageType[]) => {
      const unSavedMsg = msgItem.filter((v) => {
        return !(v.seqId in msgMapRef.current);
      });

      // 说明添加过了
      unSavedMsg.forEach((item) => {
        msgMapRef.current[item.seqId] = item;
      });

      checkUnShowedMsg(unSavedMsg);

      setMsgList((origin) => {
        const historyMsg: MessageModel[] = [];

        for (let i = 0; i < unSavedMsg.length; i++) {
          const item = unSavedMsg[i];
          const messageId = item.messageId;

          let hasMessageId = false;

          for (let index = historyMsg.length - 1; index >= 0; index--) {
            const element = historyMsg[index];

            if (element.messageId === messageId && element.type === item.sender) {
              hasMessageId = true;
              element.list.push(item);

              break;
            }

            historyMsg[index] = { ...element };
          }

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (!hasMessageId) {
            const newMessageModel: MessageModel = {
              messageId,
              sendUserId: item.sendUserId,
              type: item.sender,
              list: [item]
            };

            historyMsg.push(newMessageModel);
          }
        }

        return [...historyMsg, ...origin];
      });
    },
    [checkUnShowedMsg]
  );

  const getHistoryMsgList = useCallback(
    (e: Event) => {
      const event = e as CustomEvent<{ list: MessageType[]; noMore: boolean }>;

      const chatWindow = document.getElementsByClassName(`chat-message-container`)[0].parentElement;

      // 记录当前滚动位置
      const scrollTop = chatWindow!.scrollTop;
      const scrollHeightBefore = chatWindow!.scrollHeight;

      unShiftMsg(event.detail.list);

      setTimeout(() => {
        if (!chatWindow) {
          return;
        }

        const newMessagesHeight = chatWindow.scrollHeight - scrollHeightBefore;

        // 将滚动位置设置回之前记录的位置
        chatWindow.scrollTop = Math.max(scrollTop + newMessagesHeight, 38);
      }, 10);
    },
    [unShiftMsg]
  );

  useEffect(() => {
    if (messageList) {
      const defaultMsg: MessageType[] = [];

      messageList.forEach((msg) => {
        const result = transformMsg(msg);

        if (result) {
          defaultMsg.push(result);
        }
      });

      unShiftMsg(defaultMsg);
    }
  }, [messageList, unShiftMsg]);

  useEffect(() => {
    window.addEventListener(INNER_EVENT.HISTORY_MSG, getHistoryMsgList);

    return () => {
      window.removeEventListener(INNER_EVENT.HISTORY_MSG, getHistoryMsgList);
    };
  }, [getHistoryMsgList]);

  // 处理收到key memory通知后更新
  const handleKeyMemory = useMemoizedFn((event: Event) => {
    const e = event as CustomEvent;
    const data: TNotice = e.detail as TNotice;

    if (data.sourceType === ENoticeType.softMemory && data.sourceAction === 'add') {
      const custom = data.sourceCustom as ISourceCustomSoftMemory;

      if (custom.sessionId != sessionId || !custom.id) {
        return;
      }

      const exist = !!msgMapRef.current[custom.seqId];

      console.log('123123123 custom');

      if (!exist) {
        // 暂时不做处理
        return;
      }

      // 更新 msgList，创建新的对象引用以触发 React 重新渲染
      setMsgList((prevMsgList) => {
        return prevMsgList.map((msgGroup) => {
          // 找到匹配的消息组
          if (msgGroup.messageId != custom.messageId) {
            return msgGroup;
          }

          // 创建新的 list 数组
          const newList = msgGroup.list.map((item) => {
            // 找到匹配的消息项
            if (item.seqId != custom.seqId) {
              return item;
            }

            // 检查是否有 content
            if (!(item as any)?.data?.content) {
              return item;
            }

            const _target = item as MessageItem;

            // 检查是否已存在 softMemory 的 template
            const existingTemplates = _target.data.templates || [];
            let softMemoryTemplateIndex = -1;
            let softMemoryTemplate: any = null;

            for (let i = 0; i < existingTemplates.length; i++) {
              try {
                const parsed = JSON.parse(existingTemplates[i]);

                if (parsed.skillName === 'softMemory' && parsed.templateName === 'SoftMemoryIds') {
                  softMemoryTemplateIndex = i;
                  softMemoryTemplate = parsed;

                  break;
                }
              } catch (_e) {
                // 忽略解析错误
              }
            }

            const newMemoryId = { id: custom.id.toString(), type: 'SOFT_MEMORY' };
            let newTemplates: string[];

            if (softMemoryTemplateIndex !== -1 && softMemoryTemplate) {
              // 已存在 softMemory template，在其 memoryIds 中添加新项
              const existingMemoryIds = softMemoryTemplate.params?.memoryIds || [];

              // 检查是否已存在相同的 id，避免重复添加
              const isDuplicate = existingMemoryIds.some(
                (memItem: any) => memItem.id === newMemoryId.id && memItem.type === newMemoryId.type
              );

              if (isDuplicate) {
                return item; // 已存在，不需要更新
              }

              // 更新 memoryIds
              softMemoryTemplate.params.memoryIds = [...existingMemoryIds, newMemoryId];
              newTemplates = [...existingTemplates];
              newTemplates[softMemoryTemplateIndex] = JSON.stringify(softMemoryTemplate);
            } else {
              // 不存在 softMemory template，创建新的
              newTemplates = [
                ...existingTemplates,
                JSON.stringify({
                  skillName: 'softMemory',
                  templateName: 'SoftMemoryIds',
                  source: 'softMemory',
                  params: { memoryIds: [newMemoryId] },
                  isCurrentRound: true
                })
              ];
            }

            // 返回新的消息对象
            return {
              ..._target,
              data: {
                ..._target.data,
                templates: newTemplates
              }
            };
          });

          // 返回新的消息组对象
          return {
            ...msgGroup,
            list: newList
          };
        });
      });
    }
  });

  useEffect(() => {
    window.addEventListener(EVENT.WS_NOTICE, handleKeyMemory);

    return () => {
      window.removeEventListener(EVENT.WS_NOTICE, handleKeyMemory);
    };
  }, [sessionId, handleKeyMemory]);

  // 处理消息撤回
  const handleMessageRecalled = useMemoizedFn((event: Event) => {
    const e = event as CustomEvent<{
      messageId?: string;
      actionStatus?: EMessageAction;
      sendUserId?: string;
      sender?: string;
    }>;
    const { messageId, actionStatus, sendUserId, sender } = e.detail || {};
    const nextActionStatus = actionStatus ?? EMessageAction.REVOKED;

    setMsgList((prevMsgList) => {
      return prevMsgList.map((msgGroup) => {
        const isTargetByMessageId =
          messageId &&
          sendUserId &&
          sender &&
          msgGroup.messageId === messageId &&
          msgGroup.sendUserId === sendUserId &&
          msgGroup.type === sender;

        const newList = msgGroup.list.map((item) => {
          const shouldUpdateTarget = isTargetByMessageId;

          if (shouldUpdateTarget) {
            return {
              ...item,
              actionStatus: nextActionStatus
            } as MessageType;
          }

          if ((item as MessageItem).data.quote?.messageId === messageId) {
            const target = item as MessageItem;

            return {
              ...target,
              data: {
                ...target.data,
                quote: {
                  ...target.data.quote,
                  actionStatus: nextActionStatus
                }
              }
            } as MessageType;
          }

          return item;
        });

        return {
          ...msgGroup,
          list: newList
        };
      });
    });
  });

  useEffect(() => {
    window.addEventListener(EVENT.MESSAGE_RECALLED, handleMessageRecalled);

    return () => {
      window.removeEventListener(EVENT.MESSAGE_RECALLED, handleMessageRecalled);
    };
  }, [handleMessageRecalled]);

  const clearMsgList = useCallback(() => {
    setMsgList([]);
    msgMapRef.current = {};
  }, []);

  const filterMsgList = useMemo(() => {
    const newResult = msgList.filter((msg) => {
      return !unShowedMsgIdList.find((item) => {
        return msg.messageId === item.messageId && msg.type === item.type;
      });
    });

    return newResult;
  }, [msgList, unShowedMsgIdList]);

  return {
    msgList: filterMsgList,
    appendReceivedMsg,
    clearMsgList,
    appendUnShowedMsgId
  };
}
